/**
 * F-132: Mini App page for /me/portfolio.
 * Upload OR generate (image-1.5), edit caption, delete, drag-reorder.
 */
import { useEffect, useRef, useState } from 'react';
import { Layout } from '@/components/layout';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  Modal,
  Spinner,
  Textarea,
  toast,
} from '@/components/atoms';
import { specialists as api } from '@/api';
import type { PortfolioRow } from '@/api/specialists';
import { logger } from '@/utils/logger';
import { resolveMediaUrl } from '@/utils/media';
import styles from './pages.module.css';

type Mode = 'upload' | 'generate';

export function MyPortfolioPage(): JSX.Element {
  const [rows, setRows] = useState<PortfolioRow[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('upload');
  const [caption, setCaption] = useState('');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<PortfolioRow | null>(null);
  // F-180 C7: replace window.confirm with <ConfirmDialog>.
  const [deleteTarget, setDeleteTarget] = useState<PortfolioRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const draggingId = useRef<number | null>(null);

  useEffect(() => {
    setError(null);
    api
      .fetchMyPortfolio()
      .then((r) => setRows(r.results))
      .catch((err) => {
        logger.error('portfolio load failed', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setRows(null);
      });
  }, [reloadKey]);

  const onUpload = async (): Promise<void> => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error('Оберіть фото');
      return;
    }
    setBusy(true);
    try {
      const row = await api.uploadPortfolio(file, caption.slice(0, 200));
      setRows((prev) => [...(prev ?? []), row]);
      setOpen(false);
      setCaption('');
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Додано');
    } catch (err) {
      logger.error('upload failed', err);
      toast.error('Не вдалось завантажити');
    } finally {
      setBusy(false);
    }
  };

  const onGenerate = async (): Promise<void> => {
    if (!prompt.trim()) {
      toast.error('Опиши, що згенерувати');
      return;
    }
    setBusy(true);
    try {
      const row = await api.generatePortfolio(prompt, caption.slice(0, 200));
      setRows((prev) => [...(prev ?? []), row]);
      setOpen(false);
      setCaption('');
      setPrompt('');
      toast.success('Згенеровано');
    } catch (err) {
      logger.error('generate failed', err);
      toast.error('Не вдалось згенерувати (ліміт 2/день або помилка)');
    } finally {
      setBusy(false);
    }
  };

  const onSaveCaption = async (): Promise<void> => {
    if (!editing) return;
    setBusy(true);
    try {
      const row = await api.updatePortfolio(editing.id, {
        caption: editing.caption.slice(0, 200),
      });
      setRows((prev) => (prev ?? []).map((r) => (r.id === row.id ? row : r)));
      setEditing(null);
      toast.success('Збережено');
    } catch (err) {
      logger.error('patch failed', err);
      toast.error('Не вдалось зберегти');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deletePortfolio(deleteTarget.id);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      logger.error('delete failed', err);
      toast.error('Не вдалось видалити');
    } finally {
      setDeleting(false);
    }
  };

  const persistOrder = async (next: PortfolioRow[]): Promise<void> => {
    try {
      await api.reorderPortfolio(next.map((r) => r.id));
    } catch (err) {
      logger.error('reorder failed', err);
      toast.error('Не вдалось зберегти порядок');
    }
  };

  const onDragStart = (id: number) => () => {
    draggingId.current = id;
  };
  const onDragOver = (id: number) => (e: React.DragEvent): void => {
    e.preventDefault();
    const draggingFrom = draggingId.current;
    if (draggingFrom == null || draggingFrom === id || !rows) return;
    const fromIdx = rows.findIndex((r) => r.id === draggingFrom);
    const toIdx = rows.findIndex((r) => r.id === id);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = rows.slice();
    const [moved] = next.splice(fromIdx, 1);
    if (!moved) return;
    next.splice(toIdx, 0, moved);
    setRows(next);
  };
  const onDragEnd = (): void => {
    if (rows) void persistOrder(rows);
    draggingId.current = null;
  };

  if (error) {
    return (
      <Layout title="Портфоліо">
        <ErrorState onRetry={() => setReloadKey((k) => k + 1)} />
      </Layout>
    );
  }
  if (rows === null) {
    return (
      <Layout title="Портфоліо">
        <Spinner />
      </Layout>
    );
  }

  return (
    <Layout title="Портфоліо">
      <Button onClick={() => setOpen(true)}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="plus" size={18} />
          <span>Додати роботу</span>
        </span>
      </Button>

      {rows.length === 0 ? (
        <EmptyState icon="image" message="Поки порожньо. Додай першу роботу." />
      ) : (
        <div className={styles.list}>
          {rows.map((row) => (
            <Card
              key={row.id}
              draggable
              onDragStart={onDragStart(row.id)}
              onDragOver={onDragOver(row.id)}
              onDragEnd={onDragEnd}
            >
              <div style={{ display: 'flex', gap: 12 }}>
                <img
                  src={resolveMediaUrl(row.image_url)}
                  alt={row.caption}
                  width={80}
                  height={80}
                  style={{ borderRadius: 8, objectFit: 'cover' }}
                />
                <div style={{ flex: 1 }}>
                  <strong>{row.caption || 'Без підпису'}</strong>
                  <div className={styles.note}>
                    <Button
                      variant="ghost"
                      onClick={() => setEditing(row)}
                      style={{ padding: '4px 8px' }}
                    >
                      <Icon name="edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setDeleteTarget(row)}
                      style={{ padding: '4px 8px' }}
                    >
                      <Icon name="x" size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={open}
        title="Нова робота"
        onClose={() => setOpen(false)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Скасувати
            </Button>
            <Button
              disabled={busy}
              onClick={() => void (mode === 'upload' ? onUpload() : onGenerate())}
            >
              {mode === 'upload' ? 'Завантажити' : 'Згенерувати'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Button
            variant={mode === 'upload' ? 'primary' : 'ghost'}
            onClick={() => setMode('upload')}
          >
            Завантажити
          </Button>
          <Button
            variant={mode === 'generate' ? 'primary' : 'ghost'}
            onClick={() => setMode('generate')}
          >
            Згенерувати
          </Button>
        </div>
        {mode === 'upload' ? (
          <input ref={fileRef} type="file" accept="image/*" />
        ) : (
          <Textarea
            placeholder="Опиши, що зобразити (напр. 'Майстриня перукарка в Лісабоні')"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, 400))}
            rows={3}
          />
        )}
        <Input
          placeholder="Підпис (необов'язково, ≤120 символів)"
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, 200))}
          style={{ marginTop: 12 }}
        />
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Видалити роботу?"
        body={
          deleteTarget
            ? `Робота «${deleteTarget.caption || 'без підпису'}» зникне з портфоліо.`
            : undefined
        }
        confirmLabel="Видалити"
        tone="destructive"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <Modal
        open={Boolean(editing)}
        title="Редагувати підпис"
        onClose={() => setEditing(null)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Скасувати
            </Button>
            <Button disabled={busy} onClick={() => void onSaveCaption()}>
              Зберегти
            </Button>
          </>
        }
      >
        <Textarea
          value={editing?.caption ?? ''}
          onChange={(e) =>
            setEditing((prev) =>
              prev ? { ...prev, caption: e.target.value.slice(0, 200) } : prev,
            )
          }
          rows={3}
        />
      </Modal>
    </Layout>
  );
}
